import classNames from 'classnames'
import { CSSProperties, Ref, useCallback, useRef, useEffect, useState } from 'react'
import { SwiperRef } from 'swiper/react'

import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import NAVIGATION_TYPE from '@src/quince-site-common/src/constants/navigationType'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { RichText, RichTextProps } from '../../molecules'
import NavigationArrows from '../navigationArrows'
import Pagination, { PAGINATION_TYPES } from '../pagination/Pagination'
import Swiper from '../swiper'
import { DOTS_POSITION } from '../swiper/Swiper'
import { TextWithImageItem, TextWithImageItemProps } from '../textWithImageItem'
import { IMAGE_POSITION } from '../textWithImageItem/TextWithImageItem'

import Dots from './dots/Dots'
import styles from './textWithImageCarousel.module.scss'

export interface TextWithImageCarouselProps {
  sectionTitleRichTextContent?: RichTextProps
  sectionTitleDescriptionRichTextContent?: RichTextProps
  headingRichTextProps?: RichTextProps
  descriptionRichTextContent?: RichTextProps
  textWithImageSlideItems: TextWithImageItemProps[]
  navigationType?: NAVIGATION_TYPE
  className?: string
  style?: CSSProperties
  layoutType?: LAYOUT_TYPES
  trackingMetaData?: TrackingMetaData
  bulkItemViewTracker?: string
  tracker?: string
}

export const TextWithImageCarousel = ({
  className = '',
  navigationType = NAVIGATION_TYPE.PAGINATION,
  ...props
}: TextWithImageCarouselProps) => {
  const { layoutType } = props
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const [activePage, setActivePage] = useState(1)
  const [activeDot, setActiveDot] = useState(1)
  const totalPages = props.textWithImageSlideItems.length

  const reviewsSliderWrapClass = classNames(
    styles.reviewsSliderWrap,
    styles[`reviews_navigation_type__${navigationType}`],
  )
  const navigationClass = classNames(styles.arrowNavigation)

  const carouselRef: Ref<SwiperRef> | undefined = useRef(null)

  useEffect(() => {
    if (carouselRef.current?.swiper.activeIndex !== activePage - 1) {
      carouselRef.current?.swiper.slideTo(activePage - 1)
    }
  }, [activePage])

  const handleActiveDot = useCallback(
    (direction: string) => {
      if (direction === 'next') {
        if (activeDot < 4 || activePage === totalPages - 1) {
          setActiveDot(activeDot > 5 ? 5 : activeDot + 1)
        }
      } else if (direction === 'prev') {
        if (activeDot > 2 || activePage <= 2) {
          setActiveDot(activeDot <= 1 ? 1 : activeDot - 1)
        }
      }
    },
    [activePage, activeDot, totalPages],
  )

  return (
    <div className={classNames(className, styles.mainDivWrap)} style={props.style}>
      <div className={styles.headingContainer}>
        {props.sectionTitleRichTextContent && (
          <RichText
            {...props.sectionTitleRichTextContent}
            className={classNames(styles.sectionTitle, props.sectionTitleRichTextContent.className)}
          />
        )}
        {props.sectionTitleDescriptionRichTextContent && (
          <RichText
            {...props.sectionTitleDescriptionRichTextContent}
            className={classNames(
              styles.sectionTitleDescription,
              props.sectionTitleDescriptionRichTextContent.className,
            )}
          />
        )}
      </div>

      <div className={reviewsSliderWrapClass}>
        <div
          className={classNames(styles.leftSideDiv, {
            [styles.leftSideDiv__richTextWithsubText ?? '']: layoutType === LAYOUT_TYPES.RICH_TEXT_WITH_SUBTEXT,
          })}
        >
          {props.headingRichTextProps && (
            <RichText
              {...props.headingRichTextProps}
              className={classNames(
                styles.heading,

                props.headingRichTextProps.className,
              )}
            />
          )}
          {props.descriptionRichTextContent && (
            <RichText
              {...props.descriptionRichTextContent}
              className={classNames(styles.heading, props.descriptionRichTextContent.className)}
            />
          )}
          {
            <Dots
              activeDotIndex={activeDot}
              activeSlide={activePage}
              totalSlideCount={totalPages}
              className="mobileOnly"
            ></Dots>
          }
          {isDesktop && navigationType === NAVIGATION_TYPE.PAGINATION && (
            <div className={styles.sliderPaginationWrap}>
              <Pagination
                type={PAGINATION_TYPES.SECONDARY}
                page={activePage}
                trackingMetaData={props.trackingMetaData}
                onChange={setActivePage}
                totalPages={totalPages}
                className={styles.sliderPagination}
                hideArrows
              />
            </div>
          )}
        </div>
        <div className={styles.rightSideDiv}>
          <Swiper
            id="text-with-image-carousal"
            showNavigator={false}
            dotsPosition={DOTS_POSITION.TOP}
            variableWidth={isDesktop}
            numberOfVisibleSlides={isDesktop ? undefined : 1}
            afterChange={swiperInstance => {
              handleActiveDot(swiperInstance.swipeDirection)
              const currentTranslatePosition = Math.abs(swiperInstance.translate)
              const newActiveIndex =
                swiperInstance.snapGrid.findIndex((translate: number) => translate >= currentTranslatePosition) + 1
              if (newActiveIndex !== activePage) {
                setActivePage(newActiveIndex)
              }
            }}
            ref={carouselRef}
            freeMode={false}
            slideClassName={styles.rightSideDiv__slide}
            virtualOptions={{ enabled: true }}
            tracker={props.tracker}
            trackingMetaData={props.trackingMetaData}
          >
            {props.textWithImageSlideItems.map((textWithImageSlideItem: TextWithImageItemProps, index) => {
              return (
                <TextWithImageItem
                  {...textWithImageSlideItem}
                  key={textWithImageSlideItem.id}
                  imagePosition={index % 2 ? IMAGE_POSITION.UP : IMAGE_POSITION.DOWN}
                  className={classNames(textWithImageSlideItem.className, styles.reviewsSlideItem)}
                  layoutType={layoutType}
                  bulkItemViewTracker={props.bulkItemViewTracker}
                  tracker={props.tracker}
                  trackingMetaData={props.trackingMetaData}
                />
              )
            })}
          </Swiper>
          {navigationType === NAVIGATION_TYPE.ARROW && (
            <div className={navigationClass}>
              <NavigationArrows
                navOnRight
                prevButtonDisabled={activePage === 1}
                nextButtonDisabled={activePage === props.textWithImageSlideItems.length - 1}
                onNextClick={() => carouselRef.current?.swiper.slideNext()}
                onPrevClick={() => carouselRef.current?.swiper.slidePrev()}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TextWithImageCarousel
